import React, { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import axios from 'axios'
import {
  Typography,
  Button,
  CircularProgress
} from '@material-ui/core'
import { OpenInNew as OpenInNewIcon } from '@material-ui/icons'
import GroupsTab from './GroupsTab'
import ItemsTab from './ItemsTab'
import MatchWorkspace from './MatchWorkspace'
import MaterialsInlineTab from './MaterialsInlineTab'
import ServiceBuildTab from './ServiceBuildTab'
import ExportPreviewTab from './ExportPreviewTab'

const STEPS = [
  { key: 'groups',    label: 'Upload' },
  { key: 'items',     label: 'Review imported rows' },
  { key: 'services',  label: 'Match to services' },
  { key: 'materials', label: 'Normalize materials' },
  { key: 'build',     label: 'Refine service recipes' },
  { key: 'export',    label: 'Price & export' }
]

const PipelineStepper = ({ step, onStepClick }) => (
  <div className="bid-pipeline-stepper">
    {STEPS.map((s, idx) => {
      const done = idx < step
      const current = idx === step
      const future = idx > step
      return (
        <React.Fragment key={s.key}>
          {idx > 0 && <span className="pipeline-sep">&rsaquo;</span>}
          <button
            type="button"
            className={`pipeline-step${done ? ' done' : ''}${current ? ' current' : ''}${future ? ' future' : ''}`}
            onClick={() => onStepClick(idx)}
          >
            <span className="pipeline-dot">{done ? '✓' : idx + 1}</span>
            <span className="pipeline-label">{s.label}</span>
          </button>
        </React.Fragment>
      )
    })}
  </div>
)

const BidResponseWorkspace = ({ projectId, packageId, url }) => {
  const history = useHistory()
  const [pkg, setPkg] = useState(null)
  const [projectName, setProjectName] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [step, setStep] = useState(1)
  const [itemsGroupFilter, setItemsGroupFilter] = useState('all')
  const handleGroupClick = (groupId) => {
    setItemsGroupFilter(groupId || 'all')
    setStep(1)
  }

  const fetchPackage = useCallback(() => {
    setLoading(true)
    setError('')
    axios.get(`${url}/api/bid-estimation/bid-packages/${packageId}`)
      .then(res => {
        setPkg(res.data)
        setLoading(false)
      })
      .catch(err => {
        setError(err.response?.data?.message || 'Failed to load bid package.')
        setLoading(false)
      })
  }, [packageId, url])

  useEffect(() => {
    if (!packageId) return
    fetchPackage()
  }, [packageId, fetchPackage])

  useEffect(() => {
    if (!projectId) return
    axios.get(`${url}/api/bid-estimation/projects/${projectId}`)
      .then(res => setProjectName(res.data?.name || 'Project'))
      .catch(() => {})
  }, [projectId, url])

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Typography color="error">{error}</Typography>
      </div>
    )
  }

  const currentStep = STEPS[step]
  const displayName = projectName || 'Project Dashboard'

  return (
    <div className="hmi-page" style={{ position: 'fixed', inset: 0, zIndex: 1200, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* ── Header: breadcrumbs + title + actions ── */}
      <div className="page-head">
        <div className="crumbs">
          <a onClick={() => history.push('/bid-estimation')}>Projects</a>
          <span className="sep">&rsaquo;</span>
          {projectId && (
            <>
              <a onClick={() => history.push(`/bid-estimation/${projectId}`)}>{displayName}</a>
              <span className="sep">&rsaquo;</span>
            </>
          )}
          <span>Bid Response</span>
          <span className="sep">&rsaquo;</span>
          <span className="here">{currentStep.label}</span>
        </div>

        <div className="title-row">
          <h1>{currentStep.label}</h1>
          <span className="pill pill-neutral" style={{ fontSize: 11 }}>
            <span className="dot" />
            Step {step + 1} of {STEPS.length} &middot; Bid response &rarr; Estimate
          </span>

          <div className="actions">
            {pkg && pkg.sourceFile && pkg.sourceFile.s3Url && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNewIcon />}
                href={pkg.sourceFile.s3Url}
                target="_blank"
                rel="noopener noreferrer"
                title={pkg.sourceFile.originalFilename || 'Original file'}
              >
                View bid PDF
              </Button>
            )}

            {step > 0 && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setStep(step - 1)}
              >
                &larr; Back to {STEPS[step - 1].label.toLowerCase()}
              </Button>
            )}

            {step < STEPS.length - 1 && (
              <Button
                variant="contained"
                size="small"
                onClick={() => setStep(step + 1)}
                style={{ backgroundColor: 'var(--brand, #4a7c59)', color: '#fff' }}
              >
                Continue to {STEPS[step + 1].label.toLowerCase()} &rarr;
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ── Pipeline stepper ── */}
      <PipelineStepper step={step} onStepClick={setStep} />

      <div className="hmi-body" style={{ flex: 1, minHeight: 0, overflow: (step === 1 || step === 2) ? 'hidden' : 'auto' }}>
          {step === 0 && (
            <GroupsTab
              groups={pkg ? pkg.groups : []}
              onGroupUpdate={fetchPackage}
              onGroupClick={handleGroupClick}
              url={url}
            />
          )}
          {step === 1 && (
            <ItemsTab
              packageId={packageId}
              projectId={projectId}
              url={url}
              onItemUpdate={fetchPackage}
              initialGroupFilter={itemsGroupFilter}
              onGroupFilterChange={setItemsGroupFilter}
              sourceFile={pkg ? pkg.sourceFile : null}
            />
          )}
          {step === 2 && <MatchWorkspace packageId={packageId} projectId={projectId} url={url} onUpdate={fetchPackage} />}
          {step === 3 && <MaterialsInlineTab projectId={projectId} url={url} />}
          {step === 4 && <ServiceBuildTab projectId={projectId} packageId={packageId} url={url} />}
          {step === 5 && <ExportPreviewTab packageId={packageId} url={url} />}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url
})

export default connect(mapStateToProps)(BidResponseWorkspace)
